import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  Node,
  NodeListResponse,
  NodeDetailResponse,
  CreateNodeRequest,
  CreateNodeResponse,
  UpdateNodeRequest,
  NodeHealthResponse,
  NodeFilters,
  TestPleskRequest,
  TestPleskResponse,
  PleskDomainListResponse,
  RefreshPleskDomainsResponse,
  RotateAgentTokenResponse,
} from '@/types/node';

/**
 * Nodes API functions for the CDN Manager
 */
export const nodesApi = {
  /**
   * Fetch all CDN nodes
   */
  async getAll(filters?: NodeFilters, options?: RequestOptions): Promise<NodeListResponse> {
    const params: Record<string, string> = {};

    if (filters?.status) {
      params.status = filters.status;
    }
    if (filters?.mode) {
      params.mode = filters.mode;
    }
    if (filters?.search) {
      params.search = filters.search;
    }

    const response = await apiClient.get<NodeListResponse>('/nodes', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single node by ID
   */
  async getById(id: string, options?: RequestOptions): Promise<NodeDetailResponse> {
    const response = await apiClient.get<NodeDetailResponse>(`/nodes/${id}`, {
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Create a new CDN node
   */
  async create(data: CreateNodeRequest): Promise<CreateNodeResponse> {
    const response = await apiClient.post<CreateNodeResponse>('/nodes', data);
    return response.data;
  },

  /**
   * Update an existing node
   */
  async update(id: string, data: UpdateNodeRequest): Promise<Node> {
    const response = await apiClient.put<Node>(`/nodes/${id}`, data);
    return response.data;
  },

  /**
   * Delete a node
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/nodes/${id}`);
  },

  /**
   * Check node health
   */
  async checkHealth(id: string): Promise<NodeHealthResponse> {
    const response = await apiClient.post<NodeHealthResponse>(`/nodes/${id}/health`);
    return response.data;
  },

  /**
   * Test Plesk connection
   */
  async testPlesk(data: TestPleskRequest): Promise<TestPleskResponse> {
    const response = await apiClient.post<TestPleskResponse>('/nodes/test-plesk', data);
    return response.data;
  },

  /**
   * Get cached Plesk domains for a node
   */
  async getPleskDomains(
    nodeId: string,
    page = 1,
    limit = 50,
    options?: RequestOptions
  ): Promise<PleskDomainListResponse> {
    const offset = (page - 1) * limit;
    const response = await apiClient.get<PleskDomainListResponse>(
      `/nodes/${nodeId}/plesk-domains`,
      { params: { limit, offset }, signal: options?.signal }
    );
    return response.data;
  },

  /**
   * Refresh Plesk domains cache for a node
   */
  async refreshPleskDomains(nodeId: string): Promise<RefreshPleskDomainsResponse> {
    const response = await apiClient.post<RefreshPleskDomainsResponse>(
      `/nodes/${nodeId}/plesk-domains/refresh`
    );
    return response.data;
  },

  /**
   * Rotate the agent API token (admin only).
   * The new token is returned once and must be set as AGENT_API_TOKEN on the node.
   */
  async rotateAgentToken(nodeId: string): Promise<RotateAgentTokenResponse> {
    const response = await apiClient.post<RotateAgentTokenResponse>(
      `/nodes/${nodeId}/agent-token/rotate`
    );
    return response.data;
  },
};
